import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { FormRef } from '@lib/components/ReactHookForm/types';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useTaskFields from 'routes/Task/utils/useFields/useTaskFields';
import useModalManagement from '@lib/hooks/useModalManagement';
import TaskAdd from 'components/Task/TaskAdd';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import SiteAreasModalTableMultiselect, {
  ModalTableMultiselectMode as SiteAreasModalTableMultiselectMode,
} from 'components/SiteAreasModalTableMultiselect/SiteAreasModalTableMultiselect';
import AssetsModalTableMultiselect, {
  ModalTableMultiselectMode as AssetsModalTableMultiselectMode,
} from 'components/AssetsModalTableMultiselect/AssetsModalTableMultiselect';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import DepartmentsModalTableMultiselect from 'components/DepartmentsModalTableMultiselect/DepartmentsModalTableMultiselect';
import { FieldNames } from './enums';
import useActions from './utils/useActions';
import useSearchListQueries from './utils/useSearchListQueries';
import {
  modals as baseModals,
  selectAreaModal,
  unselectAreaModal,
  selectAssetModal,
  unselectAssetModal,
} from './modals';

const SELECT_DOCUMENTS_MODAL = 'select-documents';
const SELECT_DEPARTMENTS_MODAL = 'select-departments';
const modals = [
  ...baseModals,
  SELECT_DOCUMENTS_MODAL,
  SELECT_DEPARTMENTS_MODAL,
];

interface Props {
  isDuplicate?: boolean;
}

export default function TaskCreateContainer({ isDuplicate }: Props) {
  const { t } = useTranslation();
  const formRef = useRef<FormRef>(undefined);
  const { pathname } = useLocation();
  const useModalManagementResult = useModalManagement(modals);

  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();
  const isDocumentationLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;

  const {
    createMutationError,
    createTaskLoading,
    fullTaskData,
    fullTaskError,
    fullTaskLoading,
    onAreasMultiselect,
    onAssetsMultiselect,
    onCloseAreasModal,
    onCloseAssetsModal,
    onCreate,
    onCreateNewFormTemplate,
    onViewFormTemplate,
    openAreasModalTableMultiselect,
    openAreasModalTableMultiunselect,
    openAssetsModalTableMultiselect,
    openAssetsModalTableMultiunselect,
  } = useActions({
    formRef,
    isDocumentationLocked,
    useModalManagementResult,
  });

  const {
    getApproversForCreateFormLoadOptions: getApproversLoadOptions,
    getAssetsLoadOptions,
    getDocumentationsLoadOptions,
    getDepartmentsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getTemplateVersionsLoadOptions,
    getTemplatesLoadOptions,
  } = useSearchListQueries();

  // Documents modal
  const openDocumentsModalTableMultiselect = useCallback(() => {
    useModalManagementResult.openModal(SELECT_DOCUMENTS_MODAL);
  }, [useModalManagementResult]);

  const onCloseDocumentsModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_DOCUMENTS_MODAL);
  }, [useModalManagementResult]);

  const onDocumentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.documentationLinks,
        values.length ? values.map((docId) => ({ value: docId })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const isDocumentsModalOpen = useModalManagementResult.isModalOpened(
    SELECT_DOCUMENTS_MODAL,
  );

  // Departments modal
  const openDepartmentsModalTableMultiselect = useCallback(() => {
    useModalManagementResult.openModal(SELECT_DEPARTMENTS_MODAL);
  }, [useModalManagementResult]);

  const onCloseDepartmentsModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_DEPARTMENTS_MODAL);
  }, [useModalManagementResult]);

  const onDepartmentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.departmentLinks,
        values.length
          ? values.map((departmentId) => ({ value: departmentId }))
          : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const isDepartmentsModalOpen = useModalManagementResult.isModalOpened(
    SELECT_DEPARTMENTS_MODAL,
  );

  const formFields = useTaskFields({
    getApproversLoadOptions,
    getAssetsLoadOptions,
    getDocumentationsLoadOptions,
    getDepartmentsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getTemplatesLoadOptions,
    getTemplateVersionsLoadOptions,
    isCreate: true,
    isCreateForm: true,
    isDocumentationLocked,
    onCreateNewFormTemplate,
    onViewFormTemplate,
    openAreasModalTableMultiselect,
    openAssetsModalTableMultiselect,
    openAreasModalTableMultiunselect,
    openAssetsModalTableMultiunselect,
    openDocumentsModalTableMultiselect,
    openDepartmentsModalTableMultiselect,
    t,
  });

  const isAreasModalOpen =
    useModalManagementResult.isModalOpened(selectAreaModal) ||
    useModalManagementResult.isModalOpened(unselectAreaModal);
  const isAssetsModalOpen =
    useModalManagementResult.isModalOpened(selectAssetModal) ||
    useModalManagementResult.isModalOpened(unselectAssetModal);

  const areasModalMode = useModalManagementResult.isModalOpened(
    unselectAreaModal,
  )
    ? SiteAreasModalTableMultiselectMode.SelectedOnly
    : SiteAreasModalTableMultiselectMode.WithTabs;
  const assetsModalMode = useModalManagementResult.isModalOpened(
    unselectAssetModal,
  )
    ? AssetsModalTableMultiselectMode.SelectedOnly
    : AssetsModalTableMultiselectMode.WithTabs;

  return (
    <>
      <BreadCrumbs
        data={getBreadCrumbsDataByKey({ pathname, t, entity: fullTaskData })}
      />
      <TaskAdd
        createMutationError={createMutationError}
        createTaskLoading={createTaskLoading}
        formFields={formFields}
        formRef={formRef}
        fullTaskData={fullTaskData}
        fullTaskError={fullTaskError}
        fullTaskLoading={fullTaskLoading}
        isDuplicate={isDuplicate}
        onSubmit={onCreate}
      />
      <SiteAreasModalTableMultiselect
        isOpen={isAreasModalOpen}
        onClose={onCloseAreasModal}
        onSelect={onAreasMultiselect}
        formRef={formRef}
        mode={areasModalMode}
      />
      <AssetsModalTableMultiselect
        isOpen={isAssetsModalOpen}
        onClose={onCloseAssetsModal}
        onSelect={onAssetsMultiselect}
        formRef={formRef}
        mode={assetsModalMode}
      />
      <DocumentsModalTableMultiselect
        isOpen={isDocumentsModalOpen}
        onClose={onCloseDocumentsModal}
        onSelect={onDocumentsSelect}
        formRef={formRef}
        fieldName={FieldNames.documentationLinks}
      />
      <DepartmentsModalTableMultiselect
        isOpen={isDepartmentsModalOpen}
        onClose={onCloseDepartmentsModal}
        onSelect={onDepartmentsSelect}
        formRef={formRef}
        fieldName={FieldNames.departmentLinks}
      />
    </>
  );
}
